import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NbAbstractAuthProvider, NbAuthResult } from '@nebular/auth';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../../environments/environment';

// https://git.bde-insa-lyon.fr/BdEINSALyon/billevent/manager/blob/01bcbd75ab037661aa43e22aa9846ecce911e10a/src/app/auth/billevent-auth.service.ts
// https://github.com/akveo/nebular/issues/58

export interface ILoginData {
  username: string;
  password: string;
  email: string;
  rememberMe: boolean;
}

export interface IRegistrationData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

export interface IResetPasswordData {
  password: string;
  passwordConfirm: string;
  // Added from URL
  id: string;
  token: string;
}

export interface IRequestPasswordData {
  email: string;
}

export interface ITokenResponse {
  refresh_token: string;
  access_token: string;
  token_type: string;
  expires_in: number;
}

// export interface IRestErrorResponse {
//   timestamp: string;
//   status: number;
//   error: string;
//   message: string;
//   path: string;
// }

export interface IAuthConfig {
  apiAddress: string;
}

@Injectable()
export class AuthProviderCrypto extends NbAbstractAuthProvider {

  constructor(
    private httpClient: HttpClient,
    // https://github.com/akveo/nebular/issues/58
    // Maximum call stack size exceeded RangeError: Maximum call stack size exceeded
    // private authService: NbAuthService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  logout(): Observable<NbAuthResult> {
    console.log('[AuthProviderCrypto] logout()');
    // return this.authService.logout('name');
    return Observable.of(new NbAuthResult(
      true,
      {},
      '/',
      false,
      'Sign out success.',
    ));
  }

  authenticate(user: ILoginData): Observable<NbAuthResult> {
    if (isDevMode()) {
      console.log('[AuthProviderCrypto] authenticate() User=' + JSON.stringify(user))
    }
    const headers: HttpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Authorization', 'Basic ' + environment.auth_http_basic_auth); // HTTP Basic Auth
    const body = `grant_type=${environment.auth_grant_type}&username=${user.email}&password=${user.password}`;

    return this.httpClient.post<ITokenResponse>(
      environment.auth_token_endpoint,
      body, {
        headers,
      },
    ).map((res: ITokenResponse) => {
      if (isDevMode()) {
        console.log('[AuthProviderCrypto] Authenticate Response=' + JSON.stringify(res))
      }
      // console.log('[AuthProviderCrypto] Storing refresh_token=' + res.refresh_token);
      // sessionStorage.setItem('crypto_refresh_token', res.refresh_token);

      return new NbAuthResult(
        true,
        res,
        '/',
        false,
        `Sign in successful!`,
        res.access_token,
      );
    }).catch((res) => {
      let errors = [];
      if (res instanceof HttpErrorResponse) {
        // errors.push(res.error.error_description);
        errors = [ 'Wrong combination of username/password.' ];
      } else {
        // console.log('[AuthProviderCrypto]' + JSON.stringify(res));
        errors.push('Something went wrong.');
      }

      return Observable.of(
        new NbAuthResult(
          false,
          res,
          null,
          errors,
        ));
    });
  }

  getConfigValue(key: string) {
    return this.config[ key ];
  }

  register(data: IRegistrationData): Observable<NbAuthResult> {
    if (isDevMode()) {
      console.log('[AuthProviderCrypto] register() User=' + JSON.stringify(data))
    }
    if (!data.terms) {
      const errors = [];
      errors.push('You must agree with terms.');
      return Observable.of(
        new NbAuthResult(
          false,
          null,
          null,
          errors,
        ));
    }

    return this.httpClient.post<boolean>(
      environment.service_security_registration,
      data,
    ).map((res: boolean) => {
      if (isDevMode()) {
        console.log('[AuthProviderCrypto] Registration Response=' + JSON.stringify(res))
      }
      // console.log('[AuthProviderCrypto] Storing refresh_token=' + res.refresh_token);
      // sessionStorage.setItem('crypto_refresh_token', res.refresh_token);

      return new NbAuthResult(
        false,
        null,
        '/',
        false,
        `Registration successful!`,
        null,
      );
    }).catch((res) => {
      let errors = [];
      if (res instanceof HttpErrorResponse) {
        errors = [ res.error.message ];
      } else {
        // console.log('[AuthProviderCrypto]' + JSON.stringify(res));
        errors.push('Something went wrong.');
      }

      return Observable.of(
        new NbAuthResult(
          false,
          res,
          null,
          errors,
        ));
    });
  }

  requestPassword(data?: IRequestPasswordData): Observable<NbAuthResult> {
    if (isDevMode()) {
      console.log('[AuthProviderCrypto] requestPassword() User=' + JSON.stringify(data))
    }

    return this.httpClient.post<boolean>(
      environment.service_security_request_password,
      data,
    ).map((res: boolean) => {
      if (isDevMode()) {
        console.log('[AuthProviderCrypto] Request password Response=' + JSON.stringify(res))
      }
      // console.log('[AuthProviderCrypto] Storing refresh_token=' + res.refresh_token);
      // sessionStorage.setItem('crypto_refresh_token', res.refresh_token);

      return new NbAuthResult(
        false,
        null,
        '/auth/forgot-password-message',
        false,
        `Request password successful!`,
        null,
      );
    }).catch((res) => {
      let errors = [];
      if (res instanceof HttpErrorResponse) {
        errors = [ res.error.message ];
      } else {
        // console.log('[AuthProviderCrypto]' + JSON.stringify(res));
        errors.push('Something went wrong.');
      }

      return Observable.of(
        new NbAuthResult(
          false,
          res,
          null,
          errors,
        ));
    });
  }

  resetPassword(data?: IResetPasswordData): Observable<NbAuthResult> {
    if (isDevMode()) {
      console.log('[AuthProviderCrypto] resetPassword() User=' + JSON.stringify(data))
    }
    // Add parameters from URL
    data.id = this.route.snapshot.queryParamMap.get('id');
    data.token = this.route.snapshot.queryParamMap.get('token');

    return this.httpClient.post<boolean>(
      environment.service_security_reset_password,
      data,
    ).map((res: boolean) => {
      if (isDevMode()) {
        console.log('[AuthProviderCrypto] Reset password Response=' + JSON.stringify(res))
      }
      // console.log('[AuthProviderCrypto] Storing refresh_token=' + res.refresh_token);
      // sessionStorage.setItem('crypto_refresh_token', res.refresh_token);

      return new NbAuthResult(
        false,
        null,
        '/auth/reset-password-message',
        false,
        `Reset password successful!`,
        null,
      );
    }).catch((res) => {
      let errors = [];
      if (res instanceof HttpErrorResponse) {
        errors = [ res.error.message ];
      } else {
        // console.log('[AuthProviderCrypto]' + JSON.stringify(res));
        errors.push('Something went wrong.');
      }

      return Observable.of(
        new NbAuthResult(
          false,
          res,
          null,
          errors,
        ));
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/pages-crypto/auth-crypto/auth-provider-crypto.ts